import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  TextField,
  makeStyles,
  createStyles,
  Theme,
  MenuItem,
} from '@bb-ui/react-library';
import { Download } from '@bb-ui/icons/dist/small';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PageTemplate } from 'components/PageTemplate';
import { ErrorMessage } from 'components/ErrorMessage';
import { debounce } from 'lodash';
import { CohortsContextProvider } from 'contexts/CohortsContext';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { LocalizationProvider, AdapterDateFns } from '@bb-ui/lab/dist/DatePicker';
import CohortDetailDialog from '../CohortDetailDialog/CohortDetailDialog';
import CreateCohortDialog from '../CreateCohortDialog/CreateCohortDialog';
import SiteSearchDialog from './SiteSearchDialog';
import CohortsTable from './CohortsTable';
import { ReportGenerationOptionsDialog } from './ReportGenerationOptionsDialog';
import { useCohortsActions } from '../hooks/useCohortsActions';
import { useSitesReportGenerator } from '../hooks/useSitesReportGenerator';

const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'af-south-1', label: 'Africa (Cape Town)' },
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    filtersContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
      gap: theme.spacing(2),
    },
    searchField: {
      flexGrow: 1,
      maxWidth: 400,
    },
    regionField: {
      minWidth: 250,
    },
    buttonsContainer: {
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'center',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(4),
    },
  }),
);

export const CohortsPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { selectedRegion, dispatch } = useCohortsContext();
  const { loading, error, refreshCohorts, searchCohorts } = useCohortsActions();

  const {
    generateCurrentRegionReport,
    generateGlobalReport,
    generateDeclinedUpgradesReport,
    isGenerating,
    progress,
    statusLabel,
  } = useSitesReportGenerator({
    availableRegions: AWS_REGIONS,
  });

  const [searchFilter, setSearchFilter] = React.useState('');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [siteSearchOpen, setSiteSearchOpen] = React.useState(false);
  const [reportDialogOpen, setReportDialogOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    refreshCohorts();
  }, [refreshCohorts]);

  const debouncedSetSearch = React.useMemo(
    () => debounce((v: string) => setSearchFilter(v), 250),
    [],
  );

  const filtered = React.useMemo(() => searchCohorts(searchFilter), [searchCohorts, searchFilter]);

  const handleRegionChange = React.useCallback(
    (newRegion: string) => {
      dispatch({ type: 'SET_REGION', payload: newRegion });
      dispatch({ type: 'SET_COHORTS', payload: [] });
      dispatch({ type: 'SET_LOADING', payload: true });
    },
    [dispatch],
  );

  React.useEffect(() => {
    refreshCohorts();
  }, [selectedRegion, refreshCohorts]);

  const openCreate = React.useCallback(() => {
    setCreateOpen(true);
  }, []);

  const closeCreate = React.useCallback(() => {
    setCreateOpen(false);
  }, []);

  const onCreate = React.useCallback(() => {
    setCreateOpen(false);
  }, []);

  const openDetail = React.useCallback((id: string) => {
    setSelectedId(id);
    setDetailOpen(true);
  }, []);

  const closeDetail = React.useCallback(() => {
    setDetailOpen(false);
    setSelectedId(null);
  }, []);

  const onCohortDeleted = React.useCallback(() => {
    setDetailOpen(false);
    setSelectedId(null);
  }, []);

  const openSiteSearch = React.useCallback(() => {
    setSiteSearchOpen(true);
  }, []);

  const closeSiteSearch = React.useCallback(() => {
    setSiteSearchOpen(false);
  }, []);

  const currentRegionLabel = React.useMemo(() => {
    const region = AWS_REGIONS.find((r) => r.value === selectedRegion);
    return region ? region.label : selectedRegion;
  }, [selectedRegion]);

  const renderContent = () => {
    if (error) {
      return (
        <ErrorMessage
          data-testid="cohort-list-error"
          title={t('cohorts.loadError', 'Error loading cohorts')}
          message={error.message}
          variant="block"
        />
      );
    }

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <LoadingIndicator data-testid="cohort-list-loading" />
        </div>
      );
    }

    return <CohortsTable cohorts={filtered} loading={loading} onViewCohort={openDetail} />;
  };

  return (
    <PageTemplate title={t('cohorts.title')} data-testid="cohort-list-page">
      <div className={classes.filtersContainer}>
        <TextField
          select
          id="region-selector"
          label={t('cohorts.region', 'Region')}
          value={selectedRegion}
          onChange={(e) => handleRegionChange(e.target.value)}
          className={classes.regionField}
          floatingLabel
        >
          {AWS_REGIONS.map((region) => (
            <MenuItem key={region.value} value={region.value}>
              {`${region.label} - ${region.value.toUpperCase()}`}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          id="search-field"
          label={t('cohorts.searchLabel', 'Search Cohorts')}
          onChange={(e) => debouncedSetSearch(e.target.value)}
          className={classes.searchField}
          floatingLabel
        />

        <div className={classes.buttonsContainer}>
          <PrimaryButton
            onClick={() => setReportDialogOpen(true)}
            variant="outlined"
            disabled={isGenerating}
            startIcon={<Download />}
          >
            {isGenerating ? t('cohorts.generating', 'Generating...') : t('cohorts.sitesReport')}
          </PrimaryButton>

          <PrimaryButton onClick={openSiteSearch} variant="outlined">
            {t('cohorts.search.title')}
          </PrimaryButton>
          <PrimaryButton onClick={openCreate}>{t('cohorts.createNew')}</PrimaryButton>
        </div>
      </div>

      {renderContent()}

      <CreateCohortDialog open={createOpen} onClose={closeCreate} onCreateSuccess={onCreate} />

      <CohortDetailDialog
        open={detailOpen}
        cohortId={selectedId}
        onClose={closeDetail}
        onCohortDeleted={onCohortDeleted}
      />

      <SiteSearchDialog open={siteSearchOpen} onClose={closeSiteSearch} />

      <ReportGenerationOptionsDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        onGenerateCurrent={generateCurrentRegionReport}
        onGenerateGlobal={generateGlobalReport}
        onGenerateDeclined={generateDeclinedUpgradesReport}
        currentRegionLabel={currentRegionLabel}
        isGenerating={isGenerating}
        progress={progress}
        statusLabel={statusLabel}
      />
    </PageTemplate>
  );
};

export const CohortsUpgrade: React.FC = () => (
  <CohortsContextProvider>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <CohortsPage />
    </LocalizationProvider>
  </CohortsContextProvider>
);

export default CohortsUpgrade;
